/**
 * SavedPage Component
 *
 * Displays user's saved/favorited properties.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from './components';
import { useSavedListings } from './hooks';

export const SavedPage: React.FC = () => {
  const { listings, isLoading, removeSaved } = useSavedListings();

  const handleRemove = (e: React.MouseEvent, listingId: string) => {
    e.preventDefault();
    e.stopPropagation();
    removeSaved(listingId);
  };

  return (
    <DashboardLayout title="Saved Properties">
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="animate-pulse">
              <div className="bg-gray-200 rounded-xl h-48 mb-3" />
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
              <div className="h-4 bg-gray-200 rounded w-1/2" />
            </div>
          ))}
        </div>
      ) : listings.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {listings.map((listing) => (
            <SavedListingCard
              key={listing.id}
              listing={listing}
              onRemove={(e) => handleRemove(e, listing.id)}
            />
          ))}
        </div>
      ) : (
        <EmptyState />
      )}
    </DashboardLayout>
  );
};

// Saved Listing Card
interface SavedListingCardProps {
  listing: {
    id: string;
    title: string;
    propertyType: string;
    photo: string;
    location: { city: string; region: string };
    pricing: { nightlyRate: number; currency: string };
    rating?: number;
    reviewCount?: number;
  };
  onRemove: (e: React.MouseEvent) => void;
}

const SavedListingCard: React.FC<SavedListingCardProps> = ({ listing, onRemove }) => (
  <Link
    to={`/listing/${listing.id}`}
    className="group block bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
  >
    {/* Image */}
    <div className="relative aspect-[4/3] overflow-hidden">
      <img
        src={listing.photo}
        alt={listing.title}
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
      />
      <button
        onClick={onRemove}
        className="absolute top-3 right-3 p-2 bg-white rounded-full shadow-md hover:bg-red-50 transition-colors"
        title="Remove from saved"
      >
        <HeartFilledIcon className="w-5 h-5 text-red-500" />
      </button>
    </div>

    {/* Content */}
    <div className="p-4">
      <p className="text-sm text-gray-500 capitalize mb-1">{listing.propertyType}</p>
      <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2">{listing.title}</h3>
      <p className="text-sm text-gray-500 mb-3">
        {listing.location.city}, {listing.location.region}
      </p>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          {listing.rating && (
            <>
              <StarIcon className="w-4 h-4 text-primary-500" />
              <span className="text-sm font-medium">{listing.rating.toFixed(2)}</span>
              {listing.reviewCount && (
                <span className="text-sm text-gray-400">({listing.reviewCount})</span>
              )}
            </>
          )}
        </div>
        <div>
          <span className="font-semibold">${listing.pricing.nightlyRate}</span>
          <span className="text-gray-500 text-sm"> / night</span>
        </div>
      </div>
    </div>
  </Link>
);

// Empty State
const EmptyState: React.FC = () => (
  <div className="text-center py-16 px-4">
    <div className="text-gray-300 mb-4 flex justify-center">
      <HeartIcon className="w-16 h-16" />
    </div>
    <h3 className="text-xl font-medium text-gray-900 mb-2">No saved properties yet</h3>
    <p className="text-gray-600 mb-6 max-w-sm mx-auto">
      Start exploring and save properties you love to find them here later.
    </p>
    <Link
      to="/"
      className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
    >
      <SearchIcon className="w-5 h-5" />
      Explore properties
    </Link>
  </div>
);

// Icons
const HeartIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
    />
  </svg>
);

const HeartFilledIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
  </svg>
);

const StarIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const SearchIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
    />
  </svg>
);

export default SavedPage;
